import { categoriesSportsLeaguesConfig, marketConfigMap } from '../configs';
import { GameCategory, MarketOutcome, PeriodType } from '../enums';
import {
	GameContext,
	InPlay,
	PeriodScoresResult,
	ScopeCompletedResolver,
	TennisMarketCloseDecision,
} from '../interfaces';
import { MatchResult } from '../models/dto/probability.dto';
import {
	isOpticOddsCompleted,
	isOpticOddsLive,
	isTerminalFixture,
	readFixtureStatus,
	readRetirementInfo,
} from './optic-odds-fixture.utils';
import {
	nbaScopeResolver,
	nbaTimeProgressResolver,
	ncaabScopeResolver,
	ncaabTimeProgressResolver,
} from './market/basketball.utils';
import {
	ncaafScopeResolver,
	ncaafTimeProgressResolver,
	nflScopeResolver,
	nflTimeProgressResolver,
} from './market/football.utils';
import { golfScopeResolver, golfTimeProgressResolver } from './market/golf.utils';
import { hockeyScopeResolver, hockeyTimeProgressResolver } from './market/hockey.utils';
import { mlbScopeResolver, mlbTimeProgressResolver } from './market/mlb.utils';
import { soccerScopeResolver, soccerTimeProgressResolver } from './market/soccer.utils';
import {
	extractNumber,
	extractSelection,
	getPlayerResult,
	resolveCondition,
} from './parse-market.utils';
import { tennisTimeProgressResolver } from './market/tennis.utils';

export const isMarketSettledAtEnd = (
	category: GameCategory,
	marketName?: string | null
): boolean => {
	if (!marketName) return false;

	return marketConfigMap[category]?.[marketName]?.isFinalRequired ?? false;
};

const scopeResolvers: Partial<Record<GameCategory, ScopeCompletedResolver>> = {
	[GameCategory.NBA]: nbaScopeResolver,
	[GameCategory.MLB]: mlbScopeResolver,
	[GameCategory.EPL]: soccerScopeResolver,
	[GameCategory.UCL]: soccerScopeResolver,
	[GameCategory.FIFA]: soccerScopeResolver,
	[GameCategory.NCAAB]: ncaabScopeResolver,
	[GameCategory.NHL]: hockeyScopeResolver,
	[GameCategory.PGA]: golfScopeResolver,
	[GameCategory.NFL]: nflScopeResolver,
	[GameCategory.NCAAF]: ncaafScopeResolver,
};

export const timeProgressResolvers: Partial<
	Record<GameCategory, (inPlay: InPlay) => number | null>
> = {
	[GameCategory.NBA]: nbaTimeProgressResolver,
	[GameCategory.MLB]: mlbTimeProgressResolver,
	[GameCategory.EPL]: soccerTimeProgressResolver,
	[GameCategory.UCL]: soccerTimeProgressResolver,
	[GameCategory.FIFA]: soccerTimeProgressResolver,
	[GameCategory.NCAAB]: ncaabTimeProgressResolver,
	[GameCategory.NHL]: hockeyTimeProgressResolver,
	[GameCategory.PGA]: golfTimeProgressResolver,
	[GameCategory.NFL]: nflTimeProgressResolver,
	[GameCategory.NCAAF]: ncaafTimeProgressResolver,
	[GameCategory.ATP]: tennisTimeProgressResolver,
	[GameCategory.WTA]: tennisTimeProgressResolver,
	[GameCategory.TENNIS]: tennisTimeProgressResolver,
};

export const HOUR_IN_MS = 60 * 60 * 1000;

const MATCH_CLOSING_OFFSETS: Partial<Record<GameCategory, number>> = {
	[GameCategory.EPL]: 5 * HOUR_IN_MS,
	[GameCategory.NFL]: 5 * HOUR_IN_MS,
	[GameCategory.UCL]: 4 * HOUR_IN_MS,
	[GameCategory.FIFA]: 4 * HOUR_IN_MS,
	[GameCategory.PGA]: 12 * HOUR_IN_MS,
	[GameCategory.ATP]: 12 * HOUR_IN_MS,
	[GameCategory.WTA]: 12 * HOUR_IN_MS,
	[GameCategory.TENNIS]: 12 * HOUR_IN_MS,
};

const DEFAULT_CLOSING_OFFSET = 5 * HOUR_IN_MS;

export const getMatchClosingTime = (startDate: Date, category: GameCategory | string): Date =>
	new Date(
		startDate.getTime() +
			(MATCH_CLOSING_OFFSETS[category as GameCategory] ?? DEFAULT_CLOSING_OFFSET)
	);

const MINUTE_IN_MS = 60 * 1000;
export const TENNIS_MARKET_RESCHEDULE_BUFFER_MS = 5 * MINUTE_IN_MS;

export const resolveTennisMarketCloseDecision = (
	now: Date,
	matchClock: MatchResult | undefined
): TennisMarketCloseDecision => {
	// Terminal fixtures must close, not reschedule: a completed match is not live and its
	// start date is in the past, so the buffer branch below would push closing_date every
	// tick forever and the markets would never settle.
	const isTerminal =
		!!matchClock && isTerminalFixture(matchClock.fixtureStatus, matchClock.retirementInfo);

	if (!matchClock || matchClock.isLive || isTerminal) {
		return { shouldClose: true };
	}

	const startDate = matchClock.startDate ? new Date(matchClock.startDate) : null;

	if (!startDate || startDate.getTime() <= now.getTime()) {
		return {
			shouldClose: false,
			newClosingDate: new Date(now.getTime() + TENNIS_MARKET_RESCHEDULE_BUFFER_MS),
		};
	}

	return { shouldClose: false, newClosingDate: startDate };
};

/** OO reports each side as an array of competitors; tennis singles has exactly one. */
const readCompetitorIds = (competitors: any): string[] =>
	Array.isArray(competitors)
		? competitors.map((c) => String(c?.id ?? '')).filter((id) => id.length > 0)
		: [];

export const getGameContext = (result: any): GameContext => {
	// Guarded reads: a walkover arrives with no scores block at all, and the old
	// destructuring threw before any status could be derived.
	const fixture = result?.score?.fixture ?? {};
	const scores = result?.score?.scores ?? {};
	const home = scores.home ?? {};
	const away = scores.away ?? {};
	const inPlay = isOpticOddsLive(result.status);

	const fixtureStatus = readFixtureStatus(result);
	const retirementInfo = readRetirementInfo(result);
	const isCompleted = isOpticOddsCompleted(result.status);
	const hasScore = home.total != null || away.total != null;

	const resultLeague = String(result?.league ?? '').toLowerCase();
	const resultSport = String(result?.sport ?? '').toLowerCase();
	const category =
		(Object.entries(categoriesSportsLeaguesConfig.categories).find(
			([, v]) =>
				v.sport.toLowerCase() === resultSport &&
				(v.league.toLowerCase() === resultLeague || v.name.toLowerCase() === resultLeague)
		)?.[0] as GameCategory) ?? null;

	const resolver = category ? scopeResolvers[category] : null;

	const isScopeCompleted = (period: PeriodType): boolean => {
		if (!resolver) return false;

		return resolver(period, { isCompleted, inPlay });
	};

	return {
		homeTeam: fixture.home_team_display ?? '',
		awayTeam: fixture.away_team_display ?? '',
		homeScore: home.total ?? 0,
		awayScore: away.total ?? 0,
		isCompleted,
		category,
		fixtureStatus,
		retirementInfo,
		hasScore,
		homeCompetitorIds: readCompetitorIds(fixture.home_competitors),
		awayCompetitorIds: readCompetitorIds(fixture.away_competitors),
		periodScores: {
			home: home.periods ?? {},
			away: away.periods ?? {},
		},
		isScopeCompleted,
	};
};

export const resolveOvertimeGeneric = (
	gc: GameContext,
	lastRegularPeriod: PeriodType,
	firstOvertimePeriod: PeriodType
): [MarketOutcome] => {
	const hasOvertimePeriod =
		gc.periodScores.home[firstOvertimePeriod] !== undefined ||
		gc.periodScores.away[firstOvertimePeriod] !== undefined;

	const lastCompleted = gc.isScopeCompleted(lastRegularPeriod);
	const isTie = gc.homeScore === gc.awayScore;

	return [resolveCondition(hasOvertimePeriod || (lastCompleted && isTie), gc.isCompleted)];
};

export const resolveMoneyline = (
	getPeriodScores: (gc: GameContext, period: PeriodType) => PeriodScoresResult,
	q: string,
	gc: GameContext,
	period: PeriodType
): [MarketOutcome] => {
	const { home, away, isCompleted } = getPeriodScores(gc, period);

	if (!isCompleted) {
		return [MarketOutcome.Pending];
	}

	const isDrawSelection = q.toLowerCase().includes('draw');

	if (isDrawSelection) {
		return [home === away ? MarketOutcome.Won : MarketOutcome.Lost];
	}

	const team = extractSelection(q, 'Will ', ['win']);

	if (home === away) {
		return [MarketOutcome.Lost];
	}

	const winner = home > away ? gc.homeTeam : gc.awayTeam;

	return [winner === team ? MarketOutcome.Won : MarketOutcome.Lost];
};

export const resolveSpread = (
	getPeriodScores: (gc: GameContext, period: PeriodType) => PeriodScoresResult,
	q: string,
	gc: GameContext,
	period: PeriodType
): [MarketOutcome] => {
	const team = extractSelection(q, 'Will ', ['win by more than', 'avoid losing by more than']);
	const spread = extractNumber(q);
	const isNegativeSpread = q.includes('win by more than');
	const appliedSpread = isNegativeSpread ? -spread : spread;

	const { home, away, isCompleted } = getPeriodScores(gc, period);

	if (!isCompleted) return [MarketOutcome.Pending];

	const isHome = team === gc.homeTeam;

	const teamScore = isHome ? home : away;
	const opponentScore = isHome ? away : home;

	const adjustedScore = teamScore + appliedSpread;

	return [adjustedScore > opponentScore ? MarketOutcome.Won : MarketOutcome.Lost];
};

export const resolveTotalPoints = (
	getPeriodScores: (gc: GameContext, period: PeriodType) => PeriodScoresResult,
	q: string,
	gc: GameContext,
	period: PeriodType
): [MarketOutcome, number] => {
	const threshold = extractNumber(q);

	const { home, away, isCompleted } = getPeriodScores(gc, period);

	const total = home + away;

	return [resolveCondition(total >= threshold, isCompleted), total];
};

export const resolveTotalPointsOddEven = (
	getPeriodScores: (gc: GameContext, period: PeriodType) => PeriodScoresResult,
	gc: GameContext,
	period: PeriodType
): [MarketOutcome] => {
	const { home, away, isCompleted } = getPeriodScores(gc, period);

	if (!isCompleted) return [MarketOutcome.Pending];

	const total = home + away;

	return [total % 2 === 0 ? MarketOutcome.Won : MarketOutcome.Lost];
};

export const resolveTeamTotal = (
	getPeriodScores: (gc: GameContext, period: PeriodType) => PeriodScoresResult,
	q: string,
	gc: GameContext,
	period: PeriodType
): [MarketOutcome, number] => {
	const threshold = extractNumber(q);
	const team = extractSelection(q, 'Will ', ['score at least']);

	const { home, away, isCompleted } = getPeriodScores(gc, period);

	const isHome = team === gc.homeTeam;
	const total = isHome ? home : away;

	return [resolveCondition(total >= threshold, isCompleted), total];
};

export const resolveFirstTeamToScore = (
	getPeriodScores: (gc: GameContext, period: PeriodType) => PeriodScoresResult,
	q: string,
	gc: GameContext,
	period: PeriodType
): [MarketOutcome] => {
	const { home, away, isCompleted } = getPeriodScores(gc, period);

	const team = extractSelection(q, 'Will ', ['score first?', 'score in']);
	const isHome = team === gc.homeTeam;

	const ownScore = isHome ? home : away;
	const opponentScore = isHome ? away : home;
	if (opponentScore > 0 && ownScore === 0) {
		return [MarketOutcome.Lost];
	}

	return [resolveCondition(ownScore > 0 && opponentScore === 0, isCompleted)];
};

export const resolveGamePlayerProp = (
	q: string,
	gc: GameContext,
	r: any,
	statKey: string,
	extractAfter: string[]
): [MarketOutcome, number?] => {
	const threshold = extractNumber(q);
	const playerName = extractSelection(q, 'Will ', extractAfter);
	const playerResult = getPlayerResult(playerName, r);

	const value = playerResult?.[statKey] ?? 0;

	return [resolveCondition(value >= threshold, gc.isCompleted), value];
};

const VENUE_NAME_MAP: Record<string, string> = {
	'Gillette Stadium': 'Boston Stadium',
	'Hard Rock Stadium': 'Miami Stadium',
	"Levi's Stadium": 'San Francisco Bay Area Stadium',
	'Lincoln Financial Field': 'Philadelphia Stadium',
	'Lumen Field': 'Seattle Stadium',
	'Mercedes-Benz Stadium': 'Atlanta Stadium',
	'MetLife Stadium': 'New York New Jersey Stadium',
	'NRG Stadium': 'Houston Stadium',
	'SoFi Stadium': 'Los Angeles Stadium',
	'AT&T Stadium': 'Dallas Stadium',
	'BC Place': 'BC Place Vancouver',
	'BMO Field': 'Toronto Stadium',
	'Estadio Akron': 'Estadio Guadalajara',
	'Estadio Azteca': 'Mexico City Stadium',
	'Estadio BBVA': 'Estadio Monterrey',
	'GEHA Field at Arrowhead Stadium': 'Kansas City Stadium',
};

export const mapVenueName = (venueName: string | null | undefined): string | null | undefined => {
	if (!venueName) return venueName;
	return VENUE_NAME_MAP[venueName] ?? venueName;
};

export const buildMatchName = (gData: any, categoryId: string): string => {
	const home = gData.home_competitors?.[0];
	const away = gData.away_competitors?.[0];

	const getLabel = (c?: { name?: string; abbreviation?: string }) =>
		categoryId === GameCategory.FIFA && c?.abbreviation ? c.abbreviation : c?.name;

	const homeLabel = getLabel(home);
	const awayLabel = getLabel(away);

	if (homeLabel && awayLabel) {
		return `${homeLabel} vs ${awayLabel}`;
	}

	if (gData.home_team_display) {
		return gData.away_team_display
			? `${gData.home_team_display} - ${gData.away_team_display}`
			: gData.home_team_display;
	}

	return `${home?.name || 'Home'} vs ${away?.name || 'Away'}`;
};

export const formatShortPlayerName = (
	firstName?: string | null,
	lastName?: string | null,
	fallbackName?: string
): string | undefined => {
	if (firstName && lastName) {
		return `${firstName.charAt(0)}. ${lastName}`;
	}

	return fallbackName;
};
